// @ts-strict-ignore
import diff from 'deep-diff';
import cloneDeep from 'lodash/cloneDeep';
import keyBy from 'lodash/keyBy';

import BooleanRuleRenderer from '~/flow/BooleanRuleRenderer';
import { detokenize } from '~/flow/expressionHelpers';
import Logician, { AND_OPERATOR, OR_OPERATOR } from '~/flow/Logician';

import {
  Actions,
  WORKFLOW_STATUS_TYPES,
} from './constants';
import type {
  Action,
  ActionOption,
  AttributeComponent,
  DataRoute,
  FlowState,
  ReasonCodeGroup,
  RecurringWorkflows,
  Services,
  Threshold,
  WorkflowVersion,
} from './types';
import type { LegacyActionOption } from '~/dashboard/views/workflows/store/engine/types';

export const ActionType = {
  RUN: 'run',
  OUTCOME: 'outcome',
  TAG: 'tag',
  INPUT: 'input',
  OUTPUT: 'output',
};

export const DiffType = {
  DELETE: 'D',
  EDIT: 'E',
  NEW: 'N',
};

// NOTE: the purpose of this function is to conditionally save optional MLA attributes. Doing so means altering the elements in the arrays that
//  utilize MLAs (i.e. thresholds and dataRoutes/outputAttributes). This is because optional, unapplied MLAs do not get persisted to the DB on save.
//  This function should only be used when saving workflows and generating workflow changes, due to its destructive nature. Calling this function all of
//  the time and/or on the flow state could have unintended, destructive side effects! For instance, if someone made changes, opened the modal for
//  persisting those changes, and realized they forgot one change, we want to ensure the Flow state has been unaltered!
export const deleteOptionalUnappliedMlaFilters = ({
  thresholds = [],
  dataRoutes = [],
}: {
  thresholds?: Threshold[];
  dataRoutes?: DataRoute[];
}) => {
  // NOTE: modified result set
  const processedThresholds = [];
  const processedDataRoutes = [];
  // NOTE: deep copy of inputs so we can modify them/delete certain data (which is important for shadow workflows like what-if and trx_validation)
  const thresholdsDeepClone: Threshold[] = cloneDeep(thresholds);
  const dataRoutesDeepClone: DataRoute[] = cloneDeep(dataRoutes);

  thresholdsDeepClone.forEach(threshold => {
    if (threshold?.leftAttrComponents) {
      threshold.leftAttrComponents = trimAttrComponents(threshold.leftAttrComponents);
    }
    if (threshold?.rightAttrComponents) {
      threshold.rightAttrComponents = trimAttrComponents(threshold.rightAttrComponents);
    }
    processedThresholds.push(threshold);
  });

  dataRoutesDeepClone.forEach(dataRoute => {
    // NOTE: not all data routes have attributes
    const filterComponents = dataRoute?.attribute?.multi_level_attribute_filter?.filter_components;
    if (filterComponents) {
      const updatedFilters = trimAttrComponents(filterComponents);
      dataRoute.attribute.multi_level_attribute_filter.filter_components = updatedFilters;
    }
    processedDataRoutes.push(dataRoute);
  });

  return { processedThresholds, processedDataRoutes };
};

// runs a deep-diff between initialFlowState and updated workflow facets and returns as object
export const generateFlowDiff = (
  initialFlowState: FlowState,
  tags: FlowState['tags'],
  outcomes: FlowState['outcomes'],
  actions: FlowState['actions'],
  actionOptions: FlowState['actionOptions'],
  thresholds: FlowState['thresholds'],
  relationships: FlowState['relationships'],
  dataRoutes: FlowState['dataRoutes'],
  matrixModels: FlowState['matrixModels'],
  sdkConfig: FlowState['sdkConfig'],
) => {
  // NOTE: Ensure optional, unapplied MLA filters are removed for accurate difference comparison
  const {
    processedThresholds,
    processedDataRoutes,
  } = deleteOptionalUnappliedMlaFilters({ thresholds, dataRoutes });
  return {
    tags: diff(
      keyBy(initialFlowState.tags, 'id'),
      keyBy(tags, 'id'),
    ),
    outcomes: diff(
      keyBy(initialFlowState.outcomes, 'id'),
      keyBy(outcomes, 'id'),
    ),
    actions: diff(
      keyBy(initialFlowState.actions, 'id'),
      keyBy(actions, 'id'),
    ),
    actionOptions: diff(
      keyBy(initialFlowState.actionOptions, 'id'),
      keyBy(actionOptions, 'id'),
    ),
    thresholds: diff(
      keyBy(initialFlowState.thresholds, 'id'),
      keyBy(processedThresholds, 'id'),
    ),
    relationships: diff(
      keyBy(initialFlowState.relationships, 'id'),
      keyBy(relationships, 'id'),
    ),
    dataRoutes: diff(
      keyBy(initialFlowState.dataRoutes, 'id'),
      keyBy(processedDataRoutes, 'id'),
    ),
    matrixModels: diff(
      keyBy(initialFlowState.matrixModels, 'matrix_model_id'),
      keyBy(matrixModels, 'matrix_model_id'),
    ),
    sdkConfig: diff(
      keyBy(initialFlowState.sdkConfig, 'sdk_config_id'),
      keyBy(sdkConfig, 'sdk_config_id'),
    ),
  };
};


// translate service failure to human readable string
export const getServiceFailureDefaultString = (type: string, value?: boolean) => {
  const action = type === ActionType.RUN ? 'Run Service' : 'Apply Tag';
  switch (value) {
    case true:
      return `${action}`;
    case false:
      return `Do Not ${action}`;
    default:
      return 'Exit Workflow';
  }
};

// gets the name of the workflow instead of the id
// for the key webhookHandlerApplicationId
export const getActionOptionString = (
  key: string,
  valueParam: any,
  serviceName: string,
  recurringWorkflows: RecurringWorkflows,
) => {
  if (key === 'webhookHandlerApplicationId') {
    if (valueParam === '') {
      return 'None';
    }
    if (!isNaN(valueParam)) {
      const workflow = recurringWorkflows[serviceName].find(
        w => w.application_id === +valueParam,
      );
      return workflow ? workflow.name : valueParam;
    }
  }
  return valueParam;
};

export const formatExpressions = (actionOptions: ActionOption[]) => {
  actionOptions
    .filter(ao => ao.key === 'expression')
    .forEach(ao => {
      ao.jsonValue = detokenize(ao.jsonValue);
      ao.value = JSON.stringify(ao.value);
    });
};

export const formatOutputValueRanges = (actionOptions: ActionOption[]) => {
  actionOptions
    .filter(ao => ao.key === 'output_value_range')
    .forEach(ao => {
      ao.value = JSON.stringify(ao.value);
    });
};

// Takes in current actionOptions, new action options to add, and a map of action options by
// action id that need to be updated, if the actionId and key both match.
export function generateUpdatedActionOptions(
  actionOptions: ActionOption[] | LegacyActionOption[],
  newActionOptions: ActionOption[],
  actionOptionsToUpdate: {
    [k: string]: Omit<ActionOption, 'id' | 'publishedAttributeToken'>;
  },
) {
  const updatedActionOptions = [...actionOptions].filter(({
    key,
    actionId,
  }) => !actionOptionsToUpdate[actionId] || actionOptionsToUpdate[actionId].key !== key);
  return [...updatedActionOptions, ...newActionOptions, ...Object.values(actionOptionsToUpdate)];
}

export const NO_CHANGE_MSG = 'Nothing changed.';

export const generateDiffString = (
  initialFlowState: FlowState,
  tags: FlowState['tags'],
  outcomes: FlowState['outcomes'],
  actions: FlowState['actions'],
  actionOptions: FlowState['actionOptions'],
  thresholds: FlowState['thresholds'],
  relationships: FlowState['relationships'],
  dataRoutes: FlowState['dataRoutes'],
  sdkConfig: FlowState['sdkConfig'],
  deps: {
    services: Services;
    reasonCodeGroups: ReasonCodeGroup[];
    recurringWorkflows: RecurringWorkflows;
  },
) => {
  // Get the diff of the service flow from the initialFlowState
  const flowDiff = generateFlowDiff(
    initialFlowState, tags, outcomes, actions, actionOptions,
    thresholds, relationships, dataRoutes, [], sdkConfig,
  );
  const diffArray = [];
  const getThresholdsString = (array: Array<string | string[]>) => (
    array.map(value => {
      if (value === OR_OPERATOR) return 'or';
      if (value === AND_OPERATOR) return 'and';
      if (Array.isArray(value)) return `(${getThresholdsString(value)})`;

      // Find the correct threshold within initial and current thresholds state since they
      // could have been deleted or created
      const threshold = thresholds.find(t => t.id === +value)
        || initialFlowState.thresholds.find(t => t.id === +value);
      return BooleanRuleRenderer.getThresholdString({
        services: deps.services,
        reasonCodeGroups: deps.reasonCodeGroups,
        tags,
        // Actions will always be current, since only created actions matter
        actions: actions.concat(initialFlowState.actions),
      }, threshold);
    })
      .join(' ')
  );


  if (flowDiff.tags !== undefined) {
    diffArray.push('\n# Tags:');
    flowDiff.tags.forEach(tagDiff => {
      if (tagDiff.kind === 'N') {
        diffArray.push(` - Added Tag '${tagDiff.rhs.tag_name}'`);
      }
    });
  }

  if (flowDiff.outcomes !== undefined) {
    diffArray.push('\n# Outcomes:');
    flowDiff.outcomes.forEach(outcomeDiff => {
      if (outcomeDiff.kind === 'N') {
        diffArray.push(` - Added Outcome '${outcomeDiff.rhs.outcome}'`);
      }
    });
  }

  // Filter out cachedChildActionIds property
  if (flowDiff.actions !== undefined) {
    flowDiff.actions = flowDiff.actions.filter(actionDiff => (
      [
        'cachedChildActionIds',
        'attributes',
        'options',
      ].indexOf(actionDiff.path[1]) === -1
    ));

    if (flowDiff.actions.length) {
      diffArray.push('\n# Actions:');
      flowDiff.actions.forEach(actionDiff => {
        if (actionDiff.kind === 'E' && {}.hasOwnProperty.call(actionDiff, 'path')) {
          // Action updated
          const action = actions.find(a => a.id === +actionDiff.path[0]);
          const actionStr = `${action.type}:${action.name}`;
          let diffText;
          if (actionDiff.path[1] === 'thresholdsRule') {
            diffText = `from:\n     "${getThresholdsString(new Logician(actionDiff.lhs).toArray())}"`
              + `\n   to:\n     "${getThresholdsString(new Logician(actionDiff.rhs).toArray())}"`;
          } else if (actionDiff.path[1] === 'serviceFailureDefault') {
            diffText = `from '${getServiceFailureDefaultString(action.type, actionDiff.lhs)}'`
              + ` to '${getServiceFailureDefaultString(action.type, actionDiff.rhs)}'`;
          } else {
            diffText = `from '${actionDiff.lhs}' to '${actionDiff.rhs}'`;
          }
          diffArray.push(` - Updated '${actionStr}' ${actionDiff.path[1]} ${diffText}`);
        } else if (actionDiff.kind === 'N') {
          // Action created
          if (actionDiff.path.length === 1 || actionDiff.path[1] !== 'attributes') {
            diffArray.push(` - Added '${actionDiff.rhs.type}:${actionDiff.rhs.name}'`);
          }
        } else if (actionDiff.kind === 'D') {
          // Action deleted
          // Find it in initial state if we only have an ID
          const deleted = typeof actionDiff.lhs === 'object'
            ? actionDiff.lhs
            : initialFlowState.actions.find(a => a.id === +actionDiff.path[0]);
          if (deleted) {
            diffArray.push(` - Deleted '${deleted.type}:${deleted.name}'`);
          }
        }
      });

      const addActions = flowDiff.actions.filter(action => (
        action.kind === 'N' && (action.path.length === 1 || action.path[1] !== 'attributes')
      ));
      if (addActions.length) {
        diffArray.push('\n# Service Failure Handling:');
        addActions.forEach(action => {
          diffArray.push(` - Added '${getServiceFailureDefaultString(action.rhs.type, action.rhs.serviceFailureDefault)}'`
            + ` for '${action.rhs.type}:${action.rhs.name}'`);
        });
      }
    }
  }

  // handle parsing actionOptions
  if (flowDiff.actionOptions !== undefined) {
    flowDiff.actionOptions = flowDiff.actionOptions.filter(optionDiff => (
      optionDiff.path[1] !== 'jsonValue'
    ));

    if (flowDiff.actionOptions.length) {
      diffArray.push('\n# Action Options:');
      flowDiff.actionOptions.forEach(optionDiff => {
        let message = '';
        if (optionDiff.kind === 'E' && {}.hasOwnProperty.call(optionDiff, 'path')) {
          const actionOption = actionOptions.find(ao => ao.id === +optionDiff.path[0]);
          const action = actions.find(a => a.id === actionOption.actionId);
          message = ` - Updated '${actionOption.key}' for Action `
            + `'${action.type}:${action.name}' from ${getActionOptionString(actionOption.key, optionDiff.lhs, action.name, deps.recurringWorkflows)}'`
            + ` to '${getActionOptionString(actionOption.key, optionDiff.rhs, action.name, deps.recurringWorkflows)}'`;
        } else if (optionDiff.kind === 'N') {
          const action = actions.find(a => a.id === optionDiff.rhs.actionId);
          message = ` - Added '${optionDiff.rhs.key}' for Action `
            + `'${action.type}:${action.name}' with value `
            + `'${getActionOptionString(optionDiff.rhs.key, optionDiff.rhs.value, action.name, deps.recurringWorkflows)}'`;
        } else if (optionDiff.kind === 'D') {
          const action = initialFlowState.actions
            .find(a => a.id === optionDiff.lhs.actionId);
          message = ` - Deleted '${optionDiff.lhs.key}' for Action `
            + `'${action.type}:${action.name}' with value `
            + `'${getActionOptionString(optionDiff.lhs.key, optionDiff.lhs.value, action.name, deps.recurringWorkflows)}'`;
        }
        diffArray.push(message);
      });
    }
  }

  // handle parsing thresholds
  if (flowDiff.thresholds !== undefined) {
    diffArray.push('\n# Thresholds:');
    const thresholdProps = {
      actions: actions.concat(initialFlowState.actions),
      services: deps.services,
      reasonCodeGroups: deps.reasonCodeGroups,
      tags,
    };
    const flowDiffThresholdIds = [];
    flowDiff.thresholds.forEach(thresholdDiff => {
      if (thresholdDiff.kind === 'E' && {}.hasOwnProperty.call(thresholdDiff, 'path')) {
        const newThreshold = thresholds.find(t => t.id === +thresholdDiff.path[0]);
        if (flowDiffThresholdIds.indexOf(newThreshold.id) === -1) {
          const newThresholdString = BooleanRuleRenderer.getThresholdString(
            thresholdProps,
            newThreshold,
          );
          const action = actions.find(a => a.id === newThreshold.actionId);
          const oldThreshold = initialFlowState.thresholds.find(t => (
            t.id === newThreshold.id));

          // This is a new threshold that deep-diff didn't catch.
          if (oldThreshold !== undefined) {
            const oldThresholdString = BooleanRuleRenderer.getThresholdString(
              thresholdProps,
              oldThreshold,
            );

            // If strings are not the same, the threshold actually changed -_-
            if (newThresholdString !== oldThresholdString) {
              const message = ` - Updated '${oldThresholdString}' for Action '${action.type}:${action.name}' to '${newThresholdString}'`;
              diffArray.push(message);
            }
          }

          flowDiffThresholdIds.push(newThreshold.id);
        }
      } else if (thresholdDiff.kind === 'N') {
        const threshold = thresholds.find(t => t.id === +thresholdDiff.path[0]);
        const thresholdString = BooleanRuleRenderer.getThresholdString(
          thresholdProps,
          threshold,
        );
        const action = actions.find(a => a.id === thresholdDiff.rhs.actionId);
        if (thresholdString && action) {
          const message = ` - Added '${thresholdString}' to Action '${action.type}:${action.name}'`;
          diffArray.push(message);
        }
      } else if (
        thresholdDiff.kind === 'D'
        // Exclude threshold MLA components from this diff check since that change will be
        // captured in the generated threshold string
        && ['leftAttrComponents', 'rightAttrComponents']
          .every(key => !thresholdDiff.path.includes(key))
      ) {
        const deleted = typeof thresholdDiff.lhs === 'object'
          ? thresholdDiff.lhs
          : initialFlowState.thresholds.find(t => t.actId === thresholdDiff.lhs);
        const thresholdString = BooleanRuleRenderer.getThresholdString(
          thresholdProps,
          deleted,
        );
        const action = actions.find(a => a.id === deleted.actionId)
          || initialFlowState.actions.find(a => a.id === deleted.actionId);

        const message = ` - Deleted '${thresholdString}' from Action '${action.type}:${action.name}'`;
        diffArray.push(message);
      }
    });
  }

  if (flowDiff.dataRoutes !== undefined) {
    flowDiff.dataRoutes = flowDiff.dataRoutes.filter(dataRouteDiff => (
      !['db_id', 'attribute', 'source', 'target'].includes(dataRouteDiff.path[1])
      || (dataRouteDiff.path.length > 2 && dataRouteDiff.path[2] === 'name')
    ));

    if (flowDiff.dataRoutes.length) {
      const getDataRouteInfo = dataRoute => {
        const source = actions.find(a => a.id === dataRoute.sourceId)
          || initialFlowState.actions.find(a => a.id === dataRoute.sourceId);
        const target = actions.find(a => a.id === dataRoute.targetId)
          || initialFlowState.actions.find(a => a.id === dataRoute.targetId);
        let data = dataRoute.category ? dataRoute.category.name : false;
        data = dataRoute.attribute ? dataRoute.attribute.name : false;
        return [source, target, data];
      };

      diffArray.push('\n# Data Routes:');
      flowDiff.dataRoutes.forEach(dataRouteDiff => {
        let message = '';
        if (['N', 'D'].indexOf(dataRouteDiff.kind) > -1) {
          const action = dataRouteDiff.kind === 'N' ? 'Added' : 'Deleted';
          const dataRoute = dataRouteDiff.kind === 'N' ? dataRouteDiff.rhs : dataRouteDiff.lhs;
          const [source, target, data] = getDataRouteInfo(dataRoute);
          const dataString = data ? `of '${data}' ` : '';
          if (!source && !target) return false;
          message = ` - ${action} data routing ${dataString}from `
            + `Action '${source.type}:${source.name}' to Action '${target.type}:${target.name}'`;
        } else {
          const dataRoute = dataRoutes.find(dr => dr.id === +dataRouteDiff.path[0]);
          const [source, target] = getDataRouteInfo(dataRoute);
          message = ` - Updated data routing of '${dataRouteDiff.lhs}' to '${dataRouteDiff.rhs}' `
            + `from Action '${source.type}:${source.name}' `
            + `to Action '${target.type}:${target.name}'`;
        }

        diffArray.push(message);
        return true;
      });
    }
  }

  if (flowDiff.sdkConfig !== undefined) {
    diffArray.push('\n# SDK Configuration:');

    const initialPlugin = initialFlowState.sdkConfig.plugin;
    const newPlugin = sdkConfig.plugin;

    if (newPlugin?.length && !initialPlugin?.length) {
      diffArray.push(' - initialized SDK Configuration');
    }
    if (newPlugin === '' && initialPlugin?.length) {
      diffArray.push(' - removed SDK Configuration');
    }
    if (newPlugin !== initialPlugin && newPlugin !== '') {
      diffArray.push(` - configured to use the ${newPlugin} plugin`);
    }

    const multiSelectCollector = {};

    const formatDisplayName = (name: string) => name.split('_').join(' ');

    const addToCollector = (slug: string, lineItem: string) => {
      if (multiSelectCollector[slug]) multiSelectCollector[slug].push(lineItem);
      else multiSelectCollector[slug] = [lineItem];
    };

    flowDiff.sdkConfig.forEach(sdkConfigDiff => {
      const {
        path,
        lhs,
        rhs,
        item,
      } = sdkConfigDiff;
      const fieldIndex = +path[1];
      const { slug, dataType, plugin } = sdkConfig.fields[fieldIndex];

      if (newPlugin !== plugin) return;

      // Account for text field changes
      if (dataType === 'text' || dataType === 'text_paragraph') {
        if (rhs || lhs) {
          diffArray.push(` - ${formatDisplayName(slug)} changed to "${rhs}"`);
        }
      }

      // Account for multiselect checkbox changes
      if (dataType === 'multiselect') {
        if (Array.isArray(rhs) && rhs?.length) {
          diffArray.push(` - ${formatDisplayName(slug)} selection changed to include: ${rhs.join(', ')}`);
        } else if (item?.rhs) {
          addToCollector(slug, item.rhs);
        } else if (rhs) {
          addToCollector(slug, rhs);
        }
      }

      // Account for checkbox changes
      if (dataType === 'checkbox' && lhs === !rhs) {
        diffArray.push(` - ${formatDisplayName(slug)} ${rhs ? 'enabled' : 'disabled'}`);
      }

      // Account for radio button or dropdown changes
      if (dataType === 'radio_button' || dataType === 'dropdown') {
        diffArray.push(` - ${formatDisplayName(slug)} changed to "${rhs}"`);
      }
    });

    Object.keys(multiSelectCollector).forEach(key => {
      diffArray.push(` - ${formatDisplayName(key)} changed to include: ${multiSelectCollector[key].join(', ')}`);
    });
  }

  return diffArray.length > 0 ? diffArray.join('\n').trim() : NO_CHANGE_MSG;
};

export const hydrateMatrixModelDataRoutes = (
  actions: Action[],
  dataRoutes: DataRoute[],
) => {
  const dataRouteMap = { update: false };
  actions.forEach(action => {
    if (action.type === Actions.MATRIX_MODEL) {
      dataRouteMap[action.id] = {};
      Object.entries(action.options).forEach(([key, value]) => {
        ['x', 'y'].forEach(axis => {
          if (key === `data_route_${axis}`) {
            const [dataRoute] = dataRoutes.filter(route => route.id === Number(value.value));
            dataRouteMap[action.id][axis] = dataRoute;
          }
        });
      });
    }
  });
  return dataRouteMap;
};

// helper function for deleteOptionalUnappliedMlaFilters, no need to export
function trimAttrComponents(attrComponents: AttributeComponent[]) {
  const savableAttrComponents = [];
  attrComponents.forEach(attrComponentData => {
    const processedAttrCompData = attrComponentData;
    let componentValue = processedAttrCompData?.value;
    const componentDataType = processedAttrCompData?.data_type
      ?? processedAttrCompData?.attribute_component?.data_type;
    if (componentValue && componentDataType === 'multi_select') {
      // NOTE: this is primarily defensive code for our current limbo state
      //    must update historical data once multi-select is released/stable
      if (!Array.isArray(componentValue)) {
        componentValue = [componentValue];
      }
      if (componentValue.length > 1 && componentValue.includes('all')) {
        componentValue = ['all'];
      }
      processedAttrCompData.value = componentValue;
    }

    const isOptional = processedAttrCompData?.is_optional
      ?? processedAttrCompData?.attribute_component?.is_optional;
    const shouldApplyFilter = processedAttrCompData?.shouldApplyFilter
      ?? processedAttrCompData?.attribute_component?.shouldApplyFilter;
    const isMockFilter = processedAttrCompData?.is_mock_mla_filter
      ?? processedAttrCompData?.attribute_component?.is_mock_mla_filter;

    if (
      !isOptional
      || (isOptional && shouldApplyFilter)
      || (isOptional && !isMockFilter && shouldApplyFilter === undefined)
    ) {
      const processedAttrComponent = processedAttrCompData;
      delete processedAttrComponent.shouldApplyFilter;
      savableAttrComponents.push(processedAttrComponent);
    }
  });
  return savableAttrComponents;
}


/**
  * Event handler for `beforeunload` when a user has made changes to the Workflow
  * via specific actions like adding a new service/attribute and then has attempted to
  * navigate away from the page without saving those changes
  * @param {Event} event - the `beforeunload` event
  * @param {boolean} hasChanges - A flag indicating if the user has made unsaved changes
  * @returns {String | undefined}
  */
export const handleBeforeUnload = (event: BeforeUnloadEvent, hasChanges: boolean) => {
  if (
    event
    && event.type === 'beforeunload'
    && hasChanges
  ) {
    event.preventDefault();

    event.returnValue = 'Leave site? Any changes you made may not be saved.';

    return 'Leave site? Any changes you made may not be saved.';
  }

  return undefined;
};


/**
 * Determines the status of a workflow version
 * @param {workflowVersion} workflowVersion
 * @returns string representing the status of a workflow version
 */
export const getWorkflowStatus = (workflowVersion: WorkflowVersion) => {
  if (workflowVersion.active_public_version && workflowVersion.public_version) {
    if (workflowVersion.active_public_version === workflowVersion.public_version) {
      return WORKFLOW_STATUS_TYPES.ACTIVE;
    }


    return WORKFLOW_STATUS_TYPES.INACTIVE;
  }

  if (workflowVersion.archived) {
    return WORKFLOW_STATUS_TYPES.ARCHIVED;
  }

  if (!workflowVersion.public_version) {
    return WORKFLOW_STATUS_TYPES.DRAFT;
  }

  return undefined;
};

